import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import ReactDOMServer from 'react-dom/server';
import { EventState } from '~/store';
import { logout } from '~/store/event';
import { startAddingParticipant, startLoadingParticipants, startLoadingScannedCount,startScanningOut  } from '~/store/participant/thunk';
import { clearLoadedParticipant, setMessageError } from '~/store/participant/participantSlice';
import { useAppDispatch, useAppSelector } from '~/hooks/hooks';
import { Message, Loader, Button } from 'rsuite';
import Swal from 'sweetalert2';
import { Scanner } from '@yudiel/react-qr-scanner';
import { Header } from '../Header/Header';
import { ModalUserMessage } from '~/components/ModalUserMessage/ModalUserMessage';

import styles from './TicketScanner.module.less';

type ScanMode = 'in' | 'out';

const TicketScanner = () => {

	const { status, id: eventId, name, errorMessage, isScanOutEnabled } = useAppSelector( (state: EventState) => state.event );
	const { lastLoaded: user, messageSaved: scannerSuccess, messageError: scannerError, isLoadedScannedIn, scannedToday } = useAppSelector( (state: EventState) => state.participant);
	const [loading, setLoading] = useState<boolean>(true);
	const [enabled, setEnabled] = useState<boolean>(false);
	const location = useLocation();
	const [scanMode] = useState<ScanMode>(
		new URLSearchParams(location.search).get('mode') === 'out' ? 'out' : 'in'
	);
	const dispatch = useAppDispatch();
	const navigate = useNavigate();

	useEffect(() => {
		if (status === 'loaded') {
			setLoading(false);
			window.document.title = name ? name : 'Ticket scanner';

			const currentDate = new Date().toJSON();
			dispatch( startLoadingParticipants( currentDate ) );
			dispatch( startLoadingScannedCount() );
		} else {
			logout('Event not loaded');
			navigate('/');
		}
	}, []);

	useEffect(() => {
		setEnabled(isLoadedScannedIn)
	}, [isLoadedScannedIn])

	useEffect(() => {
		if (scannerError && scannerError.length > 0) {
			const html = ReactDOMServer.renderToStaticMarkup(
				<ModalUserMessage user={ user } message={ scannerError } type="error" />
			);
			Swal.fire({
				title: 'Ups!',
				width: 400,
				color: '#fff',
				html: html,
				icon: 'error',
				confirmButtonText: 'Ok',
				customClass: {
					title: 'alert-title'
				}
			}).then( () => {
				dispatch( clearLoadedParticipant() );
				setLoading( false );
			});
		}
	}, [scannerError]);
		
	useEffect(() => {
		if (scannerSuccess.length > 0) {
			const html = ReactDOMServer.renderToStaticMarkup(
				<ModalUserMessage user={ user } message={ scannerSuccess } type="success" />
			);
			Swal.fire({
				title: 'Good!',
				width: 400,
				color: '#fff',
				html: html,
				icon: 'success',
				confirmButtonText: 'Ok',
				customClass: {
					title: 'alert-title'
				}
			}).then( () => {
				dispatch( clearLoadedParticipant() );
				setLoading( false );
			});
		}
	}, [scannerSuccess]);

	const getResult = async (result: string) => {
		setLoading( true );
		const currentDate = new Date().toJSON();
		if (scanMode === 'in') {
			dispatch( startAddingParticipant( result, currentDate ) );
		} else {
			dispatch( startScanningOut( result ) );
		}
	};

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const handleError = (error: any) => {
		dispatch( setMessageError( error.Message ) );
	};

	const handleGoBack = () => {
		setEnabled(false);
		navigate(-1);
	};

	if (loading) {
		return <Loader backdrop center size="lg" />;
	}

	return (
		<>
			<Header />

			<div className={styles.container}>
				<h3 className={styles.info}>
					{isScanOutEnabled
						? scanMode === 'in' ? 'Scan In participants' : 'Scan Out participants'
						: 'Scan participants'
					}
				</h3>
				{ scannedToday !== null && (
					<p className={styles['scanned-count']}>
						{`Scanned in today: ${scannedToday}`}
					</p>
				)}
				{eventId && !errorMessage ? (
					<>
						{ !enabled && <Loader center size="sm" /> }
						{ enabled && (
							<>
								<div className={styles['qr-content']}>
									<div className={styles['qr-frame']}>
										<Scanner
											scanDelay={2000}
											onError={(error) => {
												handleError(error);
											} }
											onScan={(data) => {
												if (data.length > 0 && enabled) { getResult(data[0].rawValue); }
											} }
											styles={{ container: { width: "100%" } }}
											/>
									</div>
								</div>
								<Button onClick={() => handleGoBack()}>Back</Button>
							</>
						)}
						
					</>
				) : (
					<>
						<Message type="error">{ errorMessage ?? 'Event was not found' }</Message>
					</>
				)}
			</div>
		</>
	);
};

export default TicketScanner;
